if (!customElements.get('product-form')) {
  const RETURN_LABEL_VARIANT_ID = 57879332553029;
  const getReturnLabelString = (key, fallback) => window.theme?.strings?.returnLabel?.[key] || fallback;

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      // The inline, sticky and package buttons all submit this form: one add at a time, or a
      // second click during the request adds the product (and Selleasy's add-ons) twice.
      if (this.adding) return;

      // The sticky ATC panel submits this form from outside it; give that button the feedback.
      this.submitBtn = evt.submitter?.name === 'add' ? evt.submitter : this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();
      this.clearAddedState();

      // Disable "Add to Cart" button until submission is complete.
      this.adding = true;
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      let added = false;
      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();
        const variantId = Number(formData.get('id'));
        const cartHasReturnLabel = oldCartData.items.some(
          (item) => Number(item.variant_id) === RETURN_LABEL_VARIANT_ID
        );

        if (variantId === RETURN_LABEL_VARIANT_ID) {
          const directAddMessage = getReturnLabelString(
            'directAdd',
            'Return labels can only be purchased via the return page.'
          );
          this.setErrorMsgState(directAddMessage);
          throw new Error(directAddMessage);
        }

        if (cartHasReturnLabel && variantId !== RETURN_LABEL_VARIANT_ID) {
          const cartConflictMessage = getReturnLabelString(
            'cartConflict',
            'Return labels cannot be combined with other products. Remove the return label from the cart first.'
          );
          this.setErrorMsgState(cartConflictMessage);
          throw new Error(cartConflictMessage);
        }

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        // Update cart icon count and provide visual feedback without opening the cart.
        ProductForm.updateCartIcon(data);
        ProductForm.shakeCartIcon();
        added = true;

        // If item was added from Quick Add drawer, show "Added to cart" message.
        const quickAddDrawer = this.closest('quick-add-drawer');
        if (quickAddDrawer) quickAddDrawer.addedToCart();

        setTimeout(() => {
          // Refresh cart contents in the background, but keep the drawer closed.
          if (this.cartDrawer) {
            this.cartDrawer.renderContents(data, false);
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                cartItems.refresh();
              }
            }
          }
        }, 700);

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);

        // The sticky ATC panel submits from outside this form: bring the error into view.
        if (this.errorMsg && !this.errorMsg.hidden && !this.contains(this.submitBtn)) {
          this.errorMsg.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      } finally {
        // Re-enable 'Add to Cart' button.
        this.adding = false;
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.removeAttribute('aria-disabled');
        if (added) this.showAddedState();
      }
    }

    /**
     * Turns the button green with a short "Added" label for 1.8s after a successful add.
     * Writes only into the text span (card buttons keep their icon) and restores from
     * data-add-to-cart-text, unless variant-picker.js has rewritten the label meanwhile.
     */
    showAddedState() {
      const btn = this.submitBtn;
      const label = btn.querySelector('.quick-add-btn-text') || btn;
      const addedText = `${theme.strings.addedShort} \u2713`;
      const original = btn.dataset.addToCartText || label.textContent;

      // Hold the height: the original label can wrap to two lines on narrow cards.
      btn.style.minHeight = `${btn.offsetHeight}px`;
      label.textContent = addedText;
      btn.classList.add('is-added');
      ProductForm.announce(theme.strings.addedToCart);

      // A variant change (variant-picker.js) rewrites the label or toggles disabled:
      // end the green state at once so "Slutsåld" never shows on green.
      const observer = new MutationObserver(() => {
        if (!btn.clearAddedState) return;
        // Drop the green without the fade; the reflow commits it before transitions return.
        btn.style.transition = 'none';
        btn.clearAddedState();
        void btn.offsetWidth;
        btn.style.removeProperty('transition');
      });

      btn.clearAddedState = () => {
        observer.disconnect();
        window.clearTimeout(btn.addedTimer);
        btn.classList.remove('is-added', 'is-added-out');
        btn.style.removeProperty('min-height');
        if (label.textContent === addedText) label.textContent = original;
        btn.clearAddedState = null;
      };
      btn.addedTimer = window.setTimeout(() => {
        // Fade the green and the added text out together, then swap the label back in.
        // 400ms matches the transition on .btn[name="add"] in custom.css.
        btn.classList.add('is-added-out');
        btn.classList.remove('is-added');
        btn.addedTimer = window.setTimeout(btn.clearAddedState, 400);
      }, 1800);
      observer.observe(btn, {
        childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['disabled']
      });
    }

    /**
     * Ends a running "Added" state right away, so a new add never starts from it.
     */
    clearAddedState() {
      if (this.submitBtn.clearAddedState) this.submitBtn.clearAddedState();
    }

    /**
     * Announces a message to screen readers through a shared live region.
     * @param {string} message - Text to announce.
     */
    static announce(message) {
      let region = document.getElementById('product-form-status');
      if (!region) {
        region = document.createElement('div');
        region.id = 'product-form-status';
        region.className = 'visually-hidden';
        region.setAttribute('role', 'status');
        region.setAttribute('aria-live', 'polite');
        document.body.appendChild(region);
      }
      region.textContent = '';
      window.setTimeout(() => { region.textContent = message; }, 100);
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Triggers a one-off shake on the header cart icon to confirm add-to-cart.
     */
    static shakeCartIcon() {
      const cartIcon = document.getElementById('cart-icon');
      if (!cartIcon) return;

      const animationClass = 'header__icon--cart-shake';
      const cleanup = () => {
        cartIcon.classList.remove(animationClass);
        cartIcon.style.removeProperty('--shake-delay');
      };

      cartIcon.classList.remove(animationClass);
      cartIcon.classList.add('has-motion');
      cartIcon.style.setProperty('--shake-delay', '0s');

      // Force a reflow so repeated adds restart the animation reliably.
      void cartIcon.offsetWidth;

      cartIcon.classList.add(animationClass);
      window.clearTimeout(cartIcon.shakeTimeout);
      cartIcon.shakeTimeout = window.setTimeout(cleanup, 1800);
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
